import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
} from 'react'
import type { JSX } from 'react'
import type { ReactNode } from 'react'

type ReplyDraftsContextType = {
  getDraft: (commentId: string) => string | undefined
  saveDraft: (commentId: string, text: string) => void
  clearDraft: (commentId: string) => void
}

const ReplyDraftsContext = createContext<ReplyDraftsContextType | null>(null)

export function ReplyDraftsProvider({
  children,
}: {
  children: ReactNode
}): JSX.Element {
  const [drafts, setDrafts] = useState<Map<string, string>>(new Map())

  const getDraft = useCallback(
    (commentId: string): string | undefined => {
      return drafts.get(commentId)
    },
    [drafts],
  )

  const saveDraft = useCallback((commentId: string, text: string): void => {
    setDrafts((prev) => {
      const next = new Map(prev)
      if (text) {
        next.set(commentId, text)
      } else {
        next.delete(commentId)
      }
      return next
    })
  }, [])

  const clearDraft = useCallback((commentId: string): void => {
    setDrafts((prev) => {
      const next = new Map(prev)
      next.delete(commentId)
      return next
    })
  }, [])

  const contextValue = useMemo(
    () => ({
      getDraft,
      saveDraft,
      clearDraft,
    }),
    [getDraft, saveDraft, clearDraft],
  )

  return (
    <ReplyDraftsContext.Provider value={contextValue}>
      {children}
    </ReplyDraftsContext.Provider>
  )
}

export function useReplyDrafts(): ReplyDraftsContextType {
  const context = useContext(ReplyDraftsContext)
  if (!context) {
    throw new Error('useReplyDrafts must be used within ReplyDraftsProvider')
  }
  return context
}
